import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  BookOpen, Heart, Utensils, PartyPopper, Users, Shield,
  ChevronRight, Play, Star, Target, Sparkles, ArrowRight, X,
  Volume2, VolumeX, Pause
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Hls from 'hls.js';

const CF_STREAM = 'https://customer-vbbn7p39z8nnfjwk.cloudflarestream.com';

const FounderVideo = ({ uid, aspectClass }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || !uid) return;
    const hlsUrl = `${CF_STREAM}/${uid}/manifest/video.m3u8`;
    if (Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1 });
      hlsRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(vid);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (hls.levels && hls.levels.length > 0) hls.currentLevel = hls.levels.length - 1;
      });
    } else if (vid.canPlayType('application/vnd.apple.mpegurl')) {
      vid.src = hlsUrl;
    }
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [uid]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (playing) {
      vid.pause();
      setPlaying(false);
      setShowOverlay(true);
    } else {
      vid.play().then(() => { setPlaying(true); setShowOverlay(false); }).catch(() => {});
    }
  }, [playing]);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  }, []);

  return (
    <div
      className={`relative group cursor-pointer overflow-hidden rounded-xl ${aspectClass}`}
      onClick={togglePlay}
    >
      {/* Decorative border glow */}
      <div className="absolute -inset-[1px] rounded-xl bg-gradient-to-br from-colombia-yellow/30 via-white/10 to-colombia-yellow/20 z-0" />
      <div className="absolute inset-0 rounded-xl overflow-hidden z-[1]">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          muted={muted}
          playsInline
          loop
          poster={`${CF_STREAM}/${uid}/thumbnails/thumbnail.jpg?time=2s`}
        />

        {/* Cinematic vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />

        {/* Play overlay */}
        {showOverlay && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-500">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
              <Play className="w-7 h-7 md:w-8 md:h-8 text-white ml-1" fill="white" />
            </div>
          </div>
        )}

        {/* Controls — bottom right */}
        {playing && (
          <div className="absolute bottom-3 right-3 flex gap-2 z-10">
            <button
              onClick={toggleMute}
              className="w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors border border-white/10"
            >
              {muted ? <VolumeX className="w-4 h-4 text-white/80" /> : <Volume2 className="w-4 h-4 text-white/80" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const About = () => {
  const { language } = useLanguage();
  const [selectedMember, setSelectedMember] = useState(null);
  const location = useLocation();

  // Handle hash scrolling
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  // Board members data with detailed bios
  const boardMembers = [
    {
      name: 'Franco Gennaro',
      title: language === 'en' ? 'Victory Missions President' : 'Presidente de Victory Missions',
      location: 'Gainesville, FL',
      photo: 'https://www.vmissions.org/wp-content/uploads/Franco-Photo-1.jpg',
      bio: language === 'en' 
        ? "Franco Gennaro has been the President of Victory Missions for over 30 years. His heart for missions began as a young man when he first traveled to Latin America and witnessed the transformative power of the Gospel. Under his leadership, Victory Missions has expanded from one nation to cities around the world, impacting university campuses, sports teams, military personnel, and business leaders. Franco's vision for The Safe House Children's Rescue Center comes from his deep conviction that every child deserves safety, love, and the opportunity to know their God-given purpose. He lives in Gainesville, Florida with his wife Mary Lou, and together they have dedicated their lives to serving vulnerable communities."
        : "Franco Gennaro ha sido el Presidente de Victory Missions por más de 30 años. Su corazón por las misiones comenzó cuando era joven y viajó por primera vez a Latinoamérica, presenciando el poder transformador del Evangelio. Bajo su liderazgo, Victory Missions se ha expandido de una nación a ciudades alrededor del mundo, impactando campus universitarios, equipos deportivos, personal militar y líderes empresariales. La visión de Franco para The Safe House viene de su profunda convicción de que cada niño merece seguridad, amor y la oportunidad de conocer su propósito dado por Dios. Vive en Gainesville, Florida con su esposa Mary Lou, y juntos han dedicado sus vidas a servir comunidades vulnerables."
    },
    {
      name: 'Beau Wilfong',
      title: language === 'en' ? 'Business & Church Leader' : 'Líder Empresarial y de Iglesia',
      location: 'Indiana',
      photo: 'https://www.vmissions.org/wp-content/uploads/Beau1-e1660147027418.jpeg',
      bio: language === 'en'
        ? "Beau Wilfong is a business leader and active church member from Indiana whose life has been marked by a commitment to serving others. His professional success has given him a platform to give generously and advocate for causes that matter. Beau first connected with Victory Missions through a mission trip and was deeply moved by the work being done for vulnerable children in Colombia. Since then, he has been a dedicated board member, using his business expertise and connections to help expand The Safe House's reach and impact. Beau believes that true success is measured by the lives we change."
        : "Beau Wilfong es un líder empresarial y miembro activo de iglesia de Indiana cuya vida ha estado marcada por un compromiso de servir a otros. Su éxito profesional le ha dado una plataforma para dar generosamente y abogar por causas importantes. Beau se conectó por primera vez con Victory Missions a través de un viaje misionero y fue profundamente conmovido por el trabajo que se está haciendo por los niños vulnerables en Colombia. Desde entonces, ha sido un miembro dedicado de la junta, usando su experiencia empresarial y conexiones para ayudar a expandir el alcance e impacto de The Safe House."
    },
    {
      name: 'Terry Shiver',
      title: language === 'en' ? 'Director at Kingdom Life School of the Spirit' : 'Director en Kingdom Life School of the Spirit',
      location: '',
      photo: 'https://www.vmissions.org/wp-content/uploads/474908_3825884416422_1002335290_o.jpg',
      bio: language === 'en'
        ? "Terry Shiver serves as Director at Kingdom Life School of the Spirit, where he has equipped countless believers to walk in the fullness of their faith. With decades of ministry experience, Terry brings a deep understanding of spiritual development and discipleship to the board. His passion for seeing lives transformed through the power of the Holy Spirit aligns perfectly with The Safe House mission of raising children who will become world-changers. Terry believes that every child rescued from danger has the potential to become a mighty leader for God's kingdom."
        : "Terry Shiver sirve como Director en Kingdom Life School of the Spirit, donde ha equipado a innumerables creyentes para caminar en la plenitud de su fe. Con décadas de experiencia ministerial, Terry aporta una profunda comprensión del desarrollo espiritual y el discipulado a la junta. Su pasión por ver vidas transformadas a través del poder del Espíritu Santo se alinea perfectamente con la misión de The Safe House de criar niños que se convertirán en transformadores del mundo."
    },
    {
      name: 'Sam Hawkins',
      title: language === 'en' ? 'Missions Pastor' : 'Pastor de Misiones',
      location: 'Church of the King, Louisiana',
      photo: 'https://www.vmissions.org/wp-content/uploads/unnamed.png',
      bio: language === 'en'
        ? "Sam Hawkins serves as Missions Pastor at Church of the King in Louisiana, one of the fastest-growing churches in America. With a heart for global outreach, Sam has led numerous mission trips and helped mobilize hundreds of believers to serve around the world. His strategic vision for missions and his ability to connect churches with opportunities for impact make him an invaluable member of The Safe House board. Sam is passionate about building bridges between churches in the U.S. and the children in Colombia who need their support."
        : "Sam Hawkins sirve como Pastor de Misiones en Church of the King en Louisiana, una de las iglesias de más rápido crecimiento en América. Con un corazón para el alcance global, Sam ha liderado numerosos viajes misioneros y ha ayudado a movilizar cientos de creyentes para servir alrededor del mundo. Su visión estratégica para las misiones y su capacidad para conectar iglesias con oportunidades de impacto lo convierten en un miembro invaluable de la junta de The Safe House."
    },
    {
      name: 'Mary Lou Gennaro',
      title: language === 'en' ? 'Victory Missions Administrator' : 'Administradora de Victory Missions',
      location: 'Gainesville, FL',
      photo: 'https://customer-assets.emergentagent.com/job_8dfec7e9-6c80-4286-ad54-a2e446df4207/artifacts/6hyk6306_IMG_9274.jpg',
      photoPosition: 'center 15%',
      modalPhoto: 'https://customer-assets.emergentagent.com/job_1d9b8a1f-eb1a-473c-b56c-05218a9c5151/artifacts/dcchhxyh_IMG_7736.jpeg',
      bio: language === 'en'
        ? "Mary Lou Gennaro serves as Administrator for Victory Missions and has been instrumental in the organization's growth over the past three decades. Working alongside her husband Franco, Mary Lou manages the day-to-day operations and ensures that every donation reaches the children who need it most. Her attention to detail, administrative excellence, and nurturing heart have made The Safe House a place where children truly feel loved and cared for. Mary Lou believes that administrative work is ministry work, and she approaches every task as an act of service to the children."
        : "Mary Lou Gennaro sirve como Administradora de Victory Missions y ha sido fundamental en el crecimiento de la organización durante las últimas tres décadas. Trabajando junto a su esposo Franco, Mary Lou gestiona las operaciones diarias y asegura que cada donación llegue a los niños que más la necesitan. Su atención al detalle, excelencia administrativa y corazón protector han hecho de The Safe House un lugar donde los niños realmente se sienten amados y cuidados."
    },
    {
      name: 'Czar Derequito',
      title: language === 'en' ? 'Entrepreneur & Church Elder' : 'Emprendedor y Anciano de Iglesia',
      location: 'Greenhouse Church, Davie, FL',
      photo: 'https://www.vmissions.org/wp-content/uploads/czar.png',
      bio: language === 'en'
        ? "Czar Derequito is a successful entrepreneur and serves as an Elder at Greenhouse Church in Davie, Florida. His business acumen combined with his heart for serving others makes him a powerful advocate for The Safe House. Czar understands the importance of sustainable, well-managed organizations that can create lasting impact. He brings strategic thinking, financial expertise, and a passionate commitment to seeing vulnerable children protected and equipped for successful futures. His experience in both business and ministry provides a unique perspective on effective nonprofit leadership."
        : "Czar Derequito es un empresario exitoso y sirve como Anciano en Greenhouse Church en Davie, Florida. Su perspicacia empresarial combinada con su corazón para servir a otros lo convierte en un poderoso defensor de The Safe House. Czar entiende la importancia de organizaciones sostenibles y bien administradas que puedan crear un impacto duradero. Aporta pensamiento estratégico, experiencia financiera y un compromiso apasionado por ver a los niños vulnerables protegidos y equipados para futuros exitosos."
    }
  ];

  const pillars = [
    {
      icon: BookOpen,
      title: language === 'en' ? 'Faith & Purpose' : 'Fe y Propósito',
      description: language === 'en' 
        ? 'Children are grounded in Biblical truth, discovering their God given identity, developing strong character, and building a faith that guides their decisions and shapes their future.'
        : 'Los niños se fundamentan en la verdad bíblica, descubriendo su identidad dada por Dios, desarrollando un carácter fuerte y construyendo una fe que guía sus decisiones y moldea su futuro.',
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: Shield,
      title: language === 'en' ? 'Safety & Protection' : 'Seguridad y Protección',
      description: language === 'en'
        ? 'We provide a secure and loving environment where children are protected from danger and instability, allowing them to finally live, learn, and grow without fear.'
        : 'Proporcionamos un ambiente seguro y amoroso donde los niños están protegidos del peligro y la inestabilidad, permitiéndoles finalmente vivir, aprender y crecer sin miedo.',
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: BookOpen,
      title: language === 'en' ? 'Education & Growth' : 'Educación y Crecimiento',
      description: language === 'en'
        ? 'Through tutoring, mentorship, and access to computers and school resources, children receive the academic support they need to learn, excel in school, and pursue their full potential.'
        : 'A través de tutoría, mentoría y acceso a computadoras y recursos escolares, los niños reciben el apoyo académico que necesitan para aprender, sobresalir en la escuela y alcanzar su máximo potencial.',
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: Utensils,
      title: language === 'en' ? 'Nutrition & Health' : 'Nutrición y Salud',
      description: language === 'en'
        ? 'Daily nutritious meals, clean water, and attentive care ensure that children grow strong, healthy, and ready to focus on learning and personal development.'
        : 'Comidas nutritivas diarias, agua limpia y cuidado atento aseguran que los niños crezcan fuertes, saludables y listos para enfocarse en el aprendizaje y desarrollo personal.',
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: PartyPopper,
      title: language === 'en' ? 'Joy & Celebration' : 'Alegría y Celebración',
      description: language === 'en'
        ? 'Birthdays, holidays, and meaningful moments are celebrated so children can experience the joy and wonder every childhood deserves.'
        : 'Cumpleaños, fiestas y momentos significativos se celebran para que los niños puedan experimentar la alegría y el asombro que toda infancia merece.',
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-50'
    },
    {
      icon: Users,
      title: language === 'en' ? 'Mentorship & Leadership' : 'Mentoría y Liderazgo',
      description: language === 'en'
        ? 'Through consistent mentorship, life guidance, and leadership development, children grow into confident young adults equipped to break out of poverty and positively influence their communities.'
        : 'A través de mentoría constante, guía de vida y desarrollo de liderazgo, los niños crecen para convertirse en jóvenes adultos seguros, equipados para salir de la pobreza e influir positivamente en sus comunidades.',
      color: 'from-colombia-yellow to-amber-500',
      bgColor: 'bg-yellow-50'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section - Cinematic */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/c91f4c04-d0de-4534-41f1-e0e85ad2e700/public" 
            alt="Children" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-colombia-yellow/20 border border-colombia-yellow/40 rounded-full text-colombia-yellow text-sm font-semibold tracking-wider uppercase mb-6">
              {language === 'en' ? 'Our Story' : 'Nuestra Historia'}
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              {language === 'en' ? 'More Than a Mission.' : 'Más Que una Misión.'}
              <br />
              <span className="text-colombia-yellow">{language === 'en' ? 'A Calling.' : 'Un Llamado.'}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'Giving children the foundation for a transformed future!'
                : 'Dando a los niños la base para un futuro transformado!'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-sh-dark to-sh-mid p-7 rounded-3xl text-white"
            >
              <div className="w-12 h-12 bg-colombia-yellow/20 rounded-2xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-colombia-yellow" />
              </div>
              <h2 className="text-2xl font-bold mb-3">{language === 'en' ? 'Our Mission' : 'Nuestra Misión'}</h2>
              <p className="text-gray-300 text-base leading-relaxed">
                {language === 'en'
                  ? "To rescue vulnerable children from danger and provide a safe, loving environment where they are nourished, educated, and given the resources they need to learn, study, and develop their talents. Through Christian principles, mentorship, and daily care, we restore their identity, strengthen their faith, and equip them to rise as confident leaders who will transform their communities and generations to come."
                  : "Rescatar a niños vulnerables del peligro y brindarles un ambiente seguro y amoroso donde sean alimentados, educados y reciban los recursos que necesitan para aprender, estudiar y desarrollar sus talentos. A través de principios cristianos, mentoría y cuidado diario, restauramos su identidad, fortalecemos su fe y los equipamos para levantarse como líderes seguros que transformarán sus comunidades y las generaciones venideras."}
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-colombia-yellow to-amber-500 p-7 rounded-3xl text-gray-900"
            >
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-gray-900" />
              </div>
              <h2 className="text-2xl font-bold mb-3">{language === 'en' ? 'Our Vision' : 'Nuestra Visión'}</h2>
              <p className="text-gray-800 text-base leading-relaxed">
                {language === 'en'
                  ? "To establish Safe House Children's Rescue Centers throughout Colombia, creating safe environments where vulnerable children can be rescued, restored, educated, and strengthened in faith. Through these centers, a new generation will be raised to lead with faith, character, and purpose while breaking cycles of poverty, transforming communities, and helping change the future of Colombia."
                  : "Establecer Centros de Rescate Infantil Safe House en todo Colombia, creando ambientes seguros donde los niños vulnerables puedan ser rescatados, restaurados, educados y fortalecidos en la fe. A través de estos centros, se levantará una nueva generación para liderar con fe, carácter y propósito, rompiendo ciclos de pobreza, transformando comunidades y ayudando a cambiar el futuro de Colombia."}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Full-Width Photo Break */}
      <section className="relative h-[25vh] md:h-[30vh] overflow-hidden">
        <img
          src="https://www.vmissions.org/wp-content/uploads/IMG_1687-1200x682.jpg"
          alt="The Safe House community"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-transparent"></div>
      </section>

      {/* Six Pillars */}
      <section id="six-pillars" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sh-primary text-sm font-bold tracking-widest uppercase">
              {language === 'en' ? 'What We Provide' : 'Lo Que Proporcionamos'}
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-3">
              {language === 'en' ? 'The Six Pillars of Care' : 'Los Seis Pilares del Cuidado'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                viewport={{ once: true }}
                className={`${pillar.bgColor} rounded-2xl p-6 hover:shadow-xl transition-shadow duration-300`}
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-4`}>
                  <pillar.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{pillar.title}</h3>
                <p className="text-gray-600 text-sm">{pillar.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="relative py-14 overflow-hidden" id="founder" data-testid="founder-section">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-colombia-yellow/20 rounded-full filter blur-[180px]" />
        </div>

        <div className="relative z-10 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <span className="uppercase tracking-[0.3em] text-[11px] font-bold text-colombia-yellow mb-2 block">
              {language === 'en' ? 'The Man Behind the Mission' : 'El Hombre Detrás de la Misión'}
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white">
              {language === 'en' ? 'Meet the' : 'Conoce al'} <span className="text-colombia-yellow">{language === 'en' ? 'Founder' : 'Fundador'}</span>
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="flex flex-col lg:flex-row gap-8 items-start"
            >
              {/* Photo */}
              <div className="lg:w-[320px] shrink-0 mx-auto lg:mx-0">
                <div className="relative overflow-hidden rounded-2xl">
                  <div className="absolute -inset-2 bg-gradient-to-br from-colombia-yellow/30 to-transparent rounded-3xl blur-lg" />
                  <img
                    src="/images/angelo-founder.jpg"
                    alt="Angelo Gennaro - Founder"
                    className="relative w-full aspect-[3/4] rounded-2xl object-cover object-top scale-105 shadow-2xl border-2 border-white/10"
                    data-testid="founder-photo"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-2xl">
                    <h3 className="text-2xl font-black text-white">Angelo Gennaro</h3>
                    <p className="text-colombia-yellow font-semibold text-sm">{language === 'en' ? 'Founder & Director' : 'Fundador y Director'}</p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="flex-1">
                {/* Opening paragraph */}
                <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                  {language === 'en'
                    ? 'I began doing mission work in Colombia in 2022, making several trips each year and witnessing firsthand the hardships these children endured. But everything changed when I watched '
                    : 'Comencé a hacer trabajo misionero en Colombia en 2022, haciendo varios viajes cada año y presenciando de primera mano las dificultades que estos niños soportaban. Pero todo cambió cuando vi '}
                  <span className="text-white font-semibold italic">Sound of Freedom.</span>
                  {language === 'en'
                    ? ' I cried through the entire film and the story touched me deeply. In that moment, I heard God say,'
                    : ' Lloré durante toda la película y la historia me tocó profundamente. En ese momento, escuché a Dios decir,'}
                </p>

                {/* Callout 1 — The Call */}
                <div className="border-l-4 border-colombia-yellow pl-4 py-2 my-4">
                  <p className="text-lg md:text-xl font-black text-white italic">
                    {language === 'en'
                      ? '"Move to Colombia and build a rescue center for these children."'
                      : '"Múdate a Colombia y construye un centro de rescate para estos niños."'}
                  </p>
                </div>

                {/* The deeper calling */}
                <p className="text-white/90 text-base md:text-lg leading-relaxed font-medium">
                  {language === 'en'
                    ? "God spoke clearly that no longer was I meant to just visit Colombia but that I was called to "
                    : 'Dios habló claramente que ya no estaba destinado solo a visitar Colombia, sino que fui llamado a '}
                  <span className="text-colombia-yellow font-bold">
                    {language === 'en' ? 'live among the people.' : 'vivir entre la gente.'}
                  </span>
                  {language === 'en'
                    ? ' To help protect the children and be a part of their daily lives.'
                    : ' A ayudar a proteger a los niños y ser parte de sus vidas diarias.'}
                </p>

                {/* Stepping out in faith */}
                <p className="text-slate-400 text-base leading-relaxed mt-4">
                  {language === 'en'
                    ? 'There are times when God calls us to step out in faith, even without a clear plan.'
                    : 'Hay momentos en que Dios nos llama a dar un paso de fe, incluso sin un plan claro.'}
                </p>

                {/* Callout 2 — No safety net */}
                <div className="border-l-4 border-colombia-yellow pl-4 py-2 my-4">
                  <p className="text-lg md:text-xl font-black text-white italic">
                    {language === 'en'
                      ? 'No financial safety net. No support system. Just a burning conviction and a deep urgency.'
                      : 'Sin respaldo financiero. Sin sistema de apoyo. Solo una convicción ardiente y una urgencia profunda.'}
                  </p>
                </div>

                <p className="text-white/80 text-lg md:text-xl leading-relaxed font-medium">
                  {language === 'en'
                    ? 'I knew these children were vulnerable, and waiting was not an option.'
                    : 'Sabía que estos niños eran vulnerables, y esperar no era una opción.'}
                </p>

                {/* Acted fast */}
                <p className="text-slate-300 text-base leading-relaxed mt-4">
                  {language === 'en'
                    ? 'I acted fast. Within 30 days I sold my dream car Mercedes Benz and all my belongings to use those funds to begin construction on "The Safe House" Children\'s Rescue Center. '
                    : 'Actué rápido. En 30 días vendí mi auto soñado Mercedes Benz y todas mis pertenencias para usar esos fondos y comenzar la construcción del Centro de Rescate Infantil "The Safe House". '}
                  <span className="text-white font-semibold">
                    {language === 'en'
                      ? 'I left behind family, friends, and the comforts of life in the United States.'
                      : 'Dejé atrás familia, amigos y las comodidades de la vida en Estados Unidos.'}
                  </span>
                </p>

                {/* Callout 3 — No backup */}
                <div className="border-l-4 border-colombia-yellow pl-4 py-2 my-4">
                  <p className="text-lg md:text-xl font-black text-white italic">
                    {language === 'en'
                      ? 'No backup. No fallback. Just faith.'
                      : 'Sin respaldo. Sin plan B. Solo fe.'}
                  </p>
                </div>

                {/* Why I did this */}
                <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                  {language === 'en'
                    ? 'People ask why I would do this, and I say it was because '
                    : 'La gente pregunta por qué haría esto, y digo que fue porque '}
                  <span className="text-white font-semibold">
                    {language === 'en' ? 'God called me.' : 'Dios me llamó.'}
                  </span>
                  {language === 'en'
                    ? " If I didn\u2019t go, who would? If I didn\u2019t take immediate action to protect God\u2019s children, who would?"
                    : ' Si no iba yo, ¿quién lo haría? Si no tomaba acción inmediata para proteger a los hijos de Dios, ¿quién lo haría?'}
                </p>

                {/* Closing — The invitation */}
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-white text-lg md:text-xl leading-relaxed font-medium">
                    {language === 'en'
                      ? 'That decision became the beginning of something far greater than I ever imagined. And now, '
                      : 'Esa decisión se convirtió en el comienzo de algo mucho más grande de lo que jamás imaginé. Y ahora, '}
                    <span className="text-colombia-yellow font-bold">
                      {language === 'en'
                        ? 'I invite you to join me in this mission.'
                        : 'te invito a unirte a mí en esta misión.'}
                    </span>
                  </p>
                </div>

              </div>
            </motion.div>

            {/* Founder Videos */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="mt-10"
              data-testid="founder-videos"
            >
              {/* Horizontal videos — stacked full width */}
              <div className="space-y-3 mb-3">
                <FounderVideo uid="0443a3ec9e7a14f087b9a0b663030be6" aspectClass="aspect-video w-full" />
                <FounderVideo uid="fe760b9d6625700c95a02ac757c88eb2" aspectClass="aspect-video w-full" />
              </div>

              {/* Vertical videos — side by side */}
              <div className="grid grid-cols-2 gap-3">
                <FounderVideo uid="8323a9aca34f7eaffc413941a802eeb4" aspectClass="aspect-[9/16] w-full" />
                <FounderVideo uid="eab6ba93b62dc766e3acbe48c25c7ecd" aspectClass="aspect-[9/16] w-full" />
              </div>

              {/* Construction video + Walking with children photo — side by side */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <FounderVideo uid="ee6de35f325569a3d13dc4bab7f1c438" aspectClass="aspect-[9/16] w-full" />
                <div className="aspect-[9/16] w-full rounded-xl overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8dfec7e9-6c80-4286-ad54-a2e446df4207/artifacts/ks473hdy_IMG_1682.jpeg"
                    alt="Angelo walking with children in Colombia"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Safe House Rescue Center Staff - Special Featured Section */}
      <section className="relative py-14 overflow-hidden" data-testid="rescue-center-staff">
        {/* Warm gradient background with decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-30">
            <div className="absolute top-10 left-10 w-72 h-72 bg-rose-200 rounded-full filter blur-[100px]"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-200 rounded-full filter blur-[120px]"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-orange-100 rounded-full filter blur-[100px]"></div>
          </div>
        </div>
        
        {/* Decorative heart pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="absolute top-20 left-20 w-24 h-24 text-rose-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <svg className="absolute bottom-20 right-32 w-32 h-32 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <svg className="absolute top-1/3 right-20 w-20 h-20 text-rose-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </div>

        <div className="relative z-10 container mx-auto px-4">
          {/* Section Header - Special styling */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/80 backdrop-blur-sm border-2 border-rose-200 rounded-full shadow-lg mb-6">
              <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
              <span className="text-rose-600 text-sm font-bold tracking-wider uppercase">
                {language === 'en' ? 'The Heart of Our Home' : 'El Corazón de Nuestro Hogar'}
              </span>
              <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              {language === 'en' ? 'Rescue Center ' : 'Personal del Centro de '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500">{language === 'en' ? 'Staff' : 'Rescate'}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {language === 'en' 
                ? 'These are the heroes who live alongside our children every day, providing love, care, and hope in every moment.'
                : 'Estos son los héroes que viven junto a nuestros niños cada día, brindando amor, cuidado y esperanza en cada momento.'}
            </p>
          </motion.div>

          {/* Staff Grid - Featured Design */}
          <div className="max-w-5xl mx-auto">
            {/* Top Row - Sandra & Bryan (Featured with photos) */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Sandra Gomez - House Mother */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 border-2 border-rose-100"
                data-testid="staff-sandra"
              >
                <div className="absolute top-4 right-4 z-20">
                  <div className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full">
                    <span className="text-white text-xs font-bold uppercase tracking-wider">
                      {language === 'en' ? 'House Mother' : 'Madre del Hogar'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2 relative overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_1d9b8a1f-eb1a-473c-b56c-05218a9c5151/artifacts/nl4n6pry_810D41A6-FB85-4411-B347-00265DA6272A.png"
                      alt="Sandra Gomez"
                      className="w-full h-72 md:h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/50 via-transparent to-transparent"></div>
                  </div>
                  <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                    <div className="w-12 h-1.5 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full mb-4"></div>
                    <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">Sandra Gomez</h3>
                    <p className="text-rose-500 font-bold text-lg mb-4">
                      {language === 'en' ? 'The House Mother' : 'La Madre del Hogar'}
                    </p>
                    <div className="text-gray-600 leading-relaxed space-y-3 text-sm md:text-base">
                      <p>
                        {language === 'en' 
                          ? 'With a heart as big as her smile, Sandra provides the nurturing love and daily care that makes The Safe House feel like home.'
                          : 'Con un corazón tan grande como su sonrisa, Sandra brinda el amor y cuidado diario que hace que The Safe House se sienta como un hogar.'}
                      </p>
                      <p>
                        {language === 'en' 
                          ? 'Living at the center, she is present day and night to care for the children, offering guidance, comfort, and stability whenever they need it most. From preparing meals and helping with daily routines to listening, encouraging, and praying with the children, Sandra plays a vital role in creating the safe and loving environment where they can heal and grow.'
                          : 'Viviendo en el centro, está presente día y noche para cuidar a los niños, ofreciendo guía, consuelo y estabilidad cuando más lo necesitan. Desde preparar comidas y ayudar con las rutinas diarias hasta escuchar, animar y orar con los niños, Sandra juega un papel vital en crear el ambiente seguro y amoroso donde pueden sanar y crecer.'}
                      </p>
                      <p>
                        {language === 'en' 
                          ? 'For many of the children, she is more than a caregiver. She is a constant source of compassion, support, and motherly love that reminds them they are valued, protected, and never alone.'
                          : 'Para muchos de los niños, ella es más que una cuidadora. Es una fuente constante de compasión, apoyo y amor maternal que les recuerda que son valorados, protegidos y nunca están solos.'}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Bryan Gomez - Pastoral Role */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 border-2 border-amber-100"
                data-testid="staff-bryan"
              >
                <div className="absolute top-4 right-4 z-20">
                  <div className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
                    <span className="text-white text-xs font-bold uppercase tracking-wider">
                      {language === 'en' ? 'Pastoral Care' : 'Cuidado Pastoral'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2 relative overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_06d01010-ece7-457d-af33-b46fbdd11508/artifacts/adivjmym_IMG_7105.jpeg"
                      alt="Bryan Gomez"
                      className="w-full h-72 md:h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/50 via-transparent to-transparent"></div>
                  </div>
                  <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                    <div className="w-12 h-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mb-4"></div>
                    <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">Bryan Gomez</h3>
                    <p className="text-amber-600 font-bold text-lg mb-4">
                      {language === 'en' ? 'Pastoral Leader for Children' : 'Líder Pastoral para los Niños'}
                    </p>
                    <div className="text-gray-600 leading-relaxed space-y-3 text-sm md:text-base">
                      <p>
                        {language === 'en' 
                          ? "Bryan plays a vital role in guiding the children spiritually and helping them grow in their understanding of God's Word."
                          : 'Bryan juega un papel vital en guiar a los niños espiritualmente y ayudarles a crecer en su comprensión de la Palabra de Dios.'}
                      </p>
                      <p>
                        {language === 'en' 
                          ? 'Through regular Bible studies, teaching, and personal conversations, he helps the children learn the truth of Scripture and discover their identity in Christ. Bryan takes time to explain the Bible in ways they can understand, encouraging them to ask questions, grow in faith, and develop a personal relationship with God.'
                          : 'A través de estudios bíblicos regulares, enseñanza y conversaciones personales, ayuda a los niños a aprender la verdad de las Escrituras y descubrir su identidad en Cristo. Bryan se toma el tiempo para explicar la Biblia de maneras que puedan entender, animándoles a hacer preguntas, crecer en fe y desarrollar una relación personal con Dios.'}
                      </p>
                      <p>
                        {language === 'en' 
                          ? "His guidance helps lay a strong spiritual foundation in their lives, shaping their character, strengthening their faith, and helping them grow into young men and women who live with purpose, hope, and a deeper understanding of God's love."
                          : 'Su guía ayuda a establecer una base espiritual sólida en sus vidas, moldeando su carácter, fortaleciendo su fe y ayudándoles a crecer para ser jóvenes que viven con propósito, esperanza y una comprensión más profunda del amor de Dios.'}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Row - Christina & Jose (Placeholder photos) */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Christina - House Cook */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 flex items-center gap-6"
                data-testid="staff-christina"
              >
                {/* Placeholder Avatar */}
                <div className="w-24 h-24 flex-shrink-0 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-400 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                  <Utensils className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Christina</h3>
                  <p className="text-orange-500 font-semibold mb-2">
                    {language === 'en' ? 'House Cook' : 'Cocinera del Hogar'}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {language === 'en' 
                      ? 'Preparing nourishing meals with love, Christina ensures every child is fed and cared for.'
                      : 'Preparando comidas nutritivas con amor, Christina asegura que cada niño esté alimentado y cuidado.'}
                  </p>
                </div>
              </motion.div>

              {/* Jose - Maintenance & Security */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-blue-100 flex items-center gap-6"
                data-testid="staff-jose"
              >
                {/* Placeholder Avatar */}
                <div className="w-24 h-24 flex-shrink-0 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Jose</h3>
                  <p className="text-blue-500 font-semibold mb-2">
                    {language === 'en' ? 'Maintenance & Security' : 'Mantenimiento y Seguridad'}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {language === 'en' 
                      ? 'Keeping our children safe and our facilities running, Jose is the protector of our home.'
                      : 'Manteniendo a nuestros niños seguros y nuestras instalaciones funcionando, Jose es el protector de nuestro hogar.'}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Bottom quote/message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-lg text-gray-500 italic max-w-2xl mx-auto">
              {language === 'en' 
                ? '"These are the hands that hold our children, the hearts that love them, and the souls dedicated to their future."'
                : '"Estas son las manos que sostienen a nuestros niños, los corazones que los aman y las almas dedicadas a su futuro."'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* TEMPORARILY HIDDEN - Board of Directors hidden per user request, restore when ready */}
      {/* <section className="relative py-14 overflow-hidden" data-testid="board-of-directors">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950"></div>
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-colombia-yellow/20 rounded-full filter blur-[150px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/15 rounded-full filter blur-[130px]"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
              {language === 'en' ? 'Meet Our' : 'Conoce Nuestra'} <span className="text-colombia-yellow">{language === 'en' ? 'Board of Directors' : 'Junta Directiva'}</span>
            </h2>
            <p className="text-base text-gray-400 mt-1">
              {language === 'en' 
                ? <>Victory Missions (<a href="https://vmissions.org" target="_blank" rel="noopener noreferrer" className="text-colombia-yellow underline hover:text-amber-300 transition-colors">vmissions.org</a>)</>
                : <>Victory Missions (<a href="https://vmissions.org" target="_blank" rel="noopener noreferrer" className="text-colombia-yellow underline hover:text-amber-300 transition-colors">vmissions.org</a>)</>}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
            {boardMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 hover:border-colombia-yellow/40 transition-all duration-300"
                data-testid={`board-member-${index}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${member.photoPosition ? '' : 'object-top'}`}
                    style={member.photoPosition ? { objectPosition: member.photoPosition } : undefined}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                  <div className="w-8 h-0.5 bg-colombia-yellow rounded-full mb-2"></div>
                  <h3 className="text-sm sm:text-base font-bold text-white mb-0.5">{member.name}</h3>
                  <p className="text-colombia-yellow font-semibold text-xs">{member.title}</p>
                  {member.location && <p className="text-gray-400 text-xs mt-0.5">{member.location}</p>}
                  <button
                    onClick={() => setSelectedMember(member)}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-white/80 hover:text-colombia-yellow transition-colors group/btn"
                    data-testid={`learn-more-${index}`}
                  >
                    {language === 'en' ? 'Learn More' : 'Saber Más'}
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Board Member Bio Modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedMember(null)}
            data-testid="bio-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="bio-modal"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="bio-modal-close"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {/* Modal Content */}
              <div className="flex flex-col md:flex-row">
                {/* Photo */}
                <div className="md:w-2/5 relative">
                  <img
                    src={selectedMember.modalPhoto || selectedMember.photo}
                    alt={selectedMember.name}
                    className="w-full h-64 md:h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-slate-900/90 via-transparent to-transparent"></div>
                </div>

                {/* Bio Content */}
                <div className="md:w-3/5 p-6 md:p-8 overflow-y-auto max-h-[60vh] md:max-h-[70vh]">
                  <div className="w-12 h-1 bg-colombia-yellow rounded-full mb-4"></div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{selectedMember.name}</h3>
                  <p className="text-colombia-yellow font-semibold mb-1">{selectedMember.title}</p>
                  {selectedMember.location && (
                    <p className="text-gray-400 text-sm mb-6">{selectedMember.location}</p>
                  )}
                  <p className="text-gray-300 leading-relaxed text-base">
                    {selectedMember.bio}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default About;
